import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Instagram, Loader2, RefreshCw, Send, ArrowLeft, MessageCircle } from "lucide-react";

const fmt = (iso) => { try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export const InstagramInbox = () => {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadThreads = async () => {
    setLoading(true);
    try { const r = await api.instagramThreads(); setThreads(r.threads || []); }
    catch { setThreads([]); } finally { setLoading(false); }
  };
  useEffect(() => { loadThreads(); }, []);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [thread]);

  const openThread = async (pid) => {
    setActive(pid); setThread(null);
    try { setThread(await api.instagramThread(pid)); }
    catch { toast.error("Could not open conversation"); }
  };
  const sync = async () => {
    setSyncing(true);
    try { const r = await api.instagramSync(); toast.success(r.message); await loadThreads(); if (active) await openThread(active); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not sync"); }
    finally { setSyncing(false); }
  };
  const send = async () => {
    if (!text.trim() || !active) return;
    setSending(true);
    try {
      const r = await api.instagramReply(active, text.trim());
      toast.success(r.message || "Sent");
      setText("");
      await openThread(active); await loadThreads();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="ig-inbox" className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
      <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-white/10">
        <div className="text-sm font-tactical font-bold text-zinc-100 flex items-center gap-2"><Instagram size={15} className="text-pink-300" /> Instagram DMs</div>
        <button data-testid="ig-sync-button" onClick={sync} disabled={syncing} className="text-[11px] font-mono text-zinc-300 border border-white/10 rounded-lg px-2.5 py-1.5 hover:text-pink-300 inline-flex items-center gap-1.5 disabled:opacity-50">{syncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Sync</button>
      </div>

      <div className="grid sm:grid-cols-[260px_1fr] min-h-[360px]">
        {/* Thread list */}
        <div className={`border-r border-white/10 ${active ? "hidden sm:block" : ""}`}>
          {loading ? (
            <div className="flex items-center gap-2 text-zinc-500 text-xs font-mono p-4"><Loader2 size={13} className="animate-spin" /> Loading…</div>
          ) : threads.length === 0 ? (
            <div className="text-center text-zinc-600 text-xs p-6">No DMs yet. Tap <strong>Sync</strong> to pull recent conversations, or wait for new messages to arrive.</div>
          ) : threads.map((t) => (
            <button key={t.person_id} data-testid={`ig-thread-${t.person_id}`} onClick={() => openThread(t.person_id)} className={`w-full text-left px-3 py-2.5 border-b border-white/5 hover:bg-white/[0.03] ${active === t.person_id ? "bg-pink-500/10" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[13px] text-zinc-200 font-semibold truncate">{t.name}</span>
                <span className="text-[9px] text-zinc-600 font-mono shrink-0">{fmt(t.last_at)}</span>
              </div>
              <div className="text-[11px] text-zinc-500 truncate">{t.outbound ? "You: " : ""}{t.last}</div>
            </button>
          ))}
        </div>

        {/* Conversation */}
        <div className={`flex flex-col ${active ? "" : "hidden sm:flex"}`}>
          {!active ? (
            <div className="flex-1 grid place-items-center text-zinc-600 text-sm"><div className="text-center"><MessageCircle size={22} className="mx-auto mb-2 opacity-50" />Pick a conversation</div></div>
          ) : !thread ? (
            <div className="flex-1 grid place-items-center text-zinc-500 text-xs font-mono"><Loader2 size={14} className="animate-spin" /></div>
          ) : (
            <>
              <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
                <button onClick={() => { setActive(null); setThread(null); }} className="sm:hidden text-zinc-400"><ArrowLeft size={16} /></button>
                <div className="text-sm font-semibold text-zinc-100">{thread.name}</div>
                <div className="text-[10px] text-zinc-600 font-mono">{thread.handle}</div>
              </div>
              <div ref={scrollRef} data-testid="ig-thread-messages" className="flex-1 overflow-y-auto p-3 space-y-1.5 max-h-[340px]">
                {(thread.messages || []).length === 0 ? (
                  <div className="text-center text-zinc-600 text-xs py-8">No messages loaded. Tap Sync to backfill history.</div>
                ) : thread.messages.map((m, i) => (
                  <div key={i} className={`flex ${m.is_from_me ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[78%] rounded-2xl px-3 py-1.5 text-[13px] ${m.is_from_me ? "bg-gradient-to-br from-[#d62976] to-[#4f5bd5] text-white" : "bg-white/10 text-zinc-100"}`}>{m.text}</div>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/10 p-2.5 flex items-center gap-2">
                <input data-testid="ig-reply-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !sending && send()} placeholder="Message… (24h reply window applies)" className="flex-1 bg-black/40 border border-white/10 rounded-full px-3.5 py-2 text-sm text-zinc-100 outline-none focus:border-pink-400/50" />
                <button data-testid="ig-reply-send" onClick={send} disabled={sending || !text.trim()} className="w-9 h-9 grid place-items-center rounded-full bg-gradient-to-br from-[#d62976] to-[#4f5bd5] text-white disabled:opacity-40 shrink-0">{sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstagramInbox;
